import {
  Alert,
  AttributeEditor,
  Box,
  BoxProps,
  Button,
  Container,
  FormField,
  Grid,
  Header,
  Input,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import { ArenaDefinition, ArenaDimension, ArenaRubric } from '@amzn/aws-jam-typescript-client';
import { cloneDeep, isEqual } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { Nullable } from '@/src/types/common';
import {
  ARENA_MAX_DIMENSIONS,
  ARENA_MIN_DIMENSIONS,
  ARENA_TOTAL_WEIGHT,
  SaveArenaDefinition,
} from '@/src/utils/arena.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import Accordion, { AccordionItem } from '@/src/components/common/Accordion/Accordion';
import LeavePageGuard from '@/src/components/common/LeavePageGuard/LeavePageGuard';
import ArenaEditActions from './ArenaEditActions';
import styles from './Arena.module.scss';

interface ArenaDimensionsProps {
  arenaDefinition: Nullable<ArenaDefinition>;
  canEdit: boolean;
  onSave: SaveArenaDefinition;
}

const RUBRIC_LEVELS: (keyof ArenaRubric)[] = ['below', 'acceptable', 'excellent'];

const RUBRIC_COLORS: Record<keyof ArenaRubric, BoxProps.Color> = {
  excellent: 'text-status-success',
  acceptable: 'text-status-warning',
  below: 'text-status-error',
};

const ArenaDimensions: React.FC<ArenaDimensionsProps> = ({ arenaDefinition, canEdit, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<ArenaDimension[]>([]);

  const savedDimensions = arenaDefinition?.dimensions ?? [];
  const dimensions = editing ? draft : savedDimensions;

  let totalWeight = 0;

  for (const dimension of dimensions) {
    totalWeight += dimension.weight ?? 0;
  }

  const rubricLabels: Record<keyof ArenaRubric, string> = {
    below: t(i18nKeys.challenges.challengeDetails.arena.dimensions.below),
    acceptable: t(i18nKeys.challenges.challengeDetails.arena.dimensions.acceptable),
    excellent: t(i18nKeys.challenges.challengeDetails.arena.dimensions.excellent),
  };

  const handleEdit = () => {
    setDraft(cloneDeep(savedDimensions));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await onSave({ dimensions: draft });
    setSaving(false);

    if (saved) {
      setEditing(false);
    }
  };

  const updateDimension = (index: number, changes: Partial<ArenaDimension>) => {
    const updated = [...dimensions];
    updated[index] = { ...updated[index], ...changes };
    setDraft(updated);
  };

  const updateRubric = (index: number, level: keyof ArenaRubric, value: string) => {
    updateDimension(index, { rubric: { ...dimensions[index].rubric, [level]: value } });
  };

  const addDimension = () => {
    setDraft([...dimensions, { key: v4(), name: '', weight: 0, rubric: {} }]);
  };

  const removeDimension = (index: number) => {
    const updated = [...dimensions];
    updated.splice(index, 1);
    setDraft(updated);
  };

  const validationMessages = useMemo(() => {
    const messages: string[] = [];

    if (dimensions.length < ARENA_MIN_DIMENSIONS || dimensions.length > ARENA_MAX_DIMENSIONS) {
      messages.push(
        t(i18nKeys.challenges.challengeDetails.arena.dimensions.countInvalid, {
          min: ARENA_MIN_DIMENSIONS,
          max: ARENA_MAX_DIMENSIONS,
        })
      );
    }

    if (totalWeight !== ARENA_TOTAL_WEIGHT) {
      messages.push(
        t(i18nKeys.challenges.challengeDetails.arena.dimensions.totalWeightInvalid, { total: totalWeight })
      );
    }

    return messages;
  }, [dimensions.length, totalWeight, t]);

  const renderValidationAlerts = () => {
    return validationMessages.map((message) => (
      <Alert key={message} type="warning">
        {message}
      </Alert>
    ));
  };

  const renderRubricLabel = (level: keyof ArenaRubric) => {
    return (
      <Box variant="awsui-key-label" color={RUBRIC_COLORS[level]}>
        {rubricLabels[level]}
      </Box>
    );
  };

  const renderRubricView = (rubric: ArenaRubric | undefined) => {
    return (
      <SpaceBetween size="m">
        {RUBRIC_LEVELS.map((level) => (
          <div key={level}>
            {renderRubricLabel(level)}
            <Box variant="p">{rubric?.[level] || t(i18nKeys.challenges.challengeDetails.arena.notSet)}</Box>
          </div>
        ))}
      </SpaceBetween>
    );
  };

  const renderDimensionEditor = (dimension: ArenaDimension, index: number) => {
    return (
      <SpaceBetween size="m">
        <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
          <FormField label={t(i18nKeys.challenges.challengeDetails.arena.dimensions.name)} stretch>
            <Input
              value={dimension.name ?? ''}
              onChange={({ detail }) => updateDimension(index, { name: detail.value })}
            />
          </FormField>
          <FormField label={t(i18nKeys.challenges.challengeDetails.arena.dimensions.weight)} stretch>
            <Input
              type="number"
              inputMode="numeric"
              value={String(dimension.weight ?? 0)}
              onChange={({ detail }) => updateDimension(index, { weight: Number(detail.value) })}
            />
          </FormField>
        </Grid>
        <FormField label={t(i18nKeys.challenges.challengeDetails.arena.dimensions.rubric)} stretch>
          <AttributeEditor
            items={RUBRIC_LEVELS}
            hideAddButton
            addButtonText=""
            isItemRemovable={() => false}
            gridLayout={[{ rows: [[1, 5]] }]}
            definition={[
              {
                control: (level) => renderRubricLabel(level),
              },
              {
                control: (level) => (
                  <Textarea
                    className={styles.autoGrow}
                    value={dimension.rubric?.[level] ?? ''}
                    rows={2}
                    onChange={({ detail }) => updateRubric(index, level, detail.value)}
                  />
                ),
              },
            ]}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const getDimensionHeaderText = (dimension: ArenaDimension) => {
    return dimension.name || t(i18nKeys.challenges.challengeDetails.arena.dimensions.untitled);
  };

  const getDimensionWeightText = (dimension: ArenaDimension) => {
    return t(i18nKeys.challenges.challengeDetails.arena.dimensions.weightValue, { weight: dimension.weight ?? 0 });
  };

  const getDimensionViewItem = (dimension: ArenaDimension): AccordionItem => {
    return {
      key: dimension.key ?? '',
      headerText: getDimensionHeaderText(dimension),
      headerCounter: getDimensionWeightText(dimension),
      headerDescription: dimension.rubric?.excellent,
      content: renderRubricView(dimension.rubric),
    };
  };

  const getDimensionEditorItem = (dimension: ArenaDimension, index: number): AccordionItem => {
    return {
      key: dimension.key ?? '',
      headerText: getDimensionHeaderText(dimension),
      headerCounter: getDimensionWeightText(dimension),
      headerDescription: dimension.rubric?.excellent,
      headerActions: (
        <Button onClick={() => removeDimension(index)}>{t(i18nKeys.challenges.challengeDetails.arena.remove)}</Button>
      ),
      content: renderDimensionEditor(dimension, index),
    };
  };

  const renderContent = () => {
    if (!editing && dimensions.length === 0) {
      return <Box>{t(i18nKeys.challenges.challengeDetails.arena.dimensions.emptyState)}</Box>;
    }

    const accordionItems: AccordionItem[] = dimensions.map((dimension, index) => {
      if (!editing) {
        return getDimensionViewItem(dimension);
      }

      return getDimensionEditorItem(dimension, index);
    });

    // One Accordion with a stable key in both modes, so its open sections survive switching to edit mode.
    return (
      <SpaceBetween size="m">
        {editing && renderValidationAlerts()}
        <Accordion key="dimensions" allowMultiple items={accordionItems} />
        {editing && (
          <Button
            iconName="add-plus"
            onClick={addDimension}
            disabled={dimensions.length >= ARENA_MAX_DIMENSIONS}
            data-testid="arena-dimensions-add"
          >
            {t(i18nKeys.challenges.challengeDetails.arena.dimensions.add)}
          </Button>
        )}
      </SpaceBetween>
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          counter={`(${dimensions.length})`}
          description={t(i18nKeys.challenges.challengeDetails.arena.dimensions.description)}
          actions={
            <ArenaEditActions
              canEdit={canEdit}
              editing={editing}
              saving={saving}
              saveDisabled={validationMessages.length > 0}
              onEdit={handleEdit}
              onCancel={() => setEditing(false)}
              onSave={() => void handleSave()}
            />
          }
          info={
            <Box color="text-body-secondary">
              {t(i18nKeys.challenges.challengeDetails.arena.dimensions.totalWeight, { total: totalWeight })}
            </Box>
          }
        >
          {t(i18nKeys.challenges.challengeDetails.arena.dimensions.title)}
        </Header>
      }
    >
      <LeavePageGuard when={editing && !isEqual(draft, savedDimensions)} />
      {renderContent()}
    </Container>
  );
};

export default ArenaDimensions;
